import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { ExcessConcentrationChartDetailsTable } from './ExcessConcentrationChartDetailsTable';
import { ConcentrationLimitDataForReportingDate } from './types';
import { colors } from '~/packages/common-frontend/colors';
import { getDefaultDashboardDisplayEndDate } from '~/packages/common-frontend/constants';
import { generateExcessConcentrationChartConfig } from '~/packages/portfolio/ui/borrowingBase/portfolioBorrowingBaseChartConfigs';
import {
  ChartTimePeriod,
  getInitialActiveFields,
  onToggleActiveField,
  PortfolioChartConfig,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import { PortfolioReChart } from '~/packages/portfolio/ui/shared/PortfolioReChart';

type ExcessConcentrationChartProps = {
  historicalRecords: any[];
  selectedTimePeriod: ChartTimePeriod;
  loanProductMechanic: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
  multiLoanProductConcentrationLimits: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[];
};
export const ExcessConcentrationChart = ({
  historicalRecords,
  selectedTimePeriod,
  loanProductMechanic,
  multiLoanProductConcentrationLimits,
}: ExcessConcentrationChartProps) => {
  const [data, setData] = useState<ConcentrationLimitDataForReportingDate[]>(
    [],
  );

  const [activeFields, setActiveFields] = useState<
    (keyof ConcentrationLimitDataForReportingDate)[]
  >([]);

  const [chartConfig, setChartConfig] = useState<PortfolioChartConfig<string>>(
    {},
  );

  const [
    selectedBar,
    setSelectedBar,
  ] = useState<ConcentrationLimitDataForReportingDate | null>(null);

  useEffect(() => {
    const graphData: ConcentrationLimitDataForReportingDate[] = [];
    historicalRecords.forEach((record) => {
      const dataForReportingDate: ConcentrationLimitDataForReportingDate = {
        reportingDate: record.reportingdate as Util.FullDateStr,
      };
      [
        ...loanProductMechanic.concentrationLimits,
        ...multiLoanProductConcentrationLimits,
      ].forEach((limit) => {
        if (limit._config_.concentrationLimitConfig) {
          const propertyKey =
            limit._config_.concentrationLimitConfig.propertyKey;
          if (record[`clr.${propertyKey.toLowerCase()}.clk`] !== undefined) {
            dataForReportingDate[propertyKey] =
              record[`clr.${propertyKey.toLowerCase()}.clk`];
          }
        }
      });
      graphData.push(dataForReportingDate);
    });
    if (graphData.length > 0) {
      setSelectedBar(graphData[graphData.length - 1]);
    }
    setData(graphData);
    const excessConcentrationChartConfig = generateExcessConcentrationChartConfig(
      graphData,
      [
        ...loanProductMechanic.concentrationLimits,
        ...multiLoanProductConcentrationLimits,
      ],
    );
    setChartConfig(excessConcentrationChartConfig);
    const initialActiveFields = getInitialActiveFields({
      chartConfig: excessConcentrationChartConfig,
    }) as (keyof ConcentrationLimitDataForReportingDate)[];

    setActiveFields(initialActiveFields);
  }, [
    historicalRecords,
    loanProductMechanic,
    multiLoanProductConcentrationLimits,
  ]);

  // if config is empty, receivable has no excess concentration
  const isChartConfigEmpty = Object.keys(chartConfig).length === 0;

  return (
    <>
      <Box
        backgroundColor={colors.background[50]}
        mt="40px"
        border="none"
        borderRadius="8px"
        padding="40px"
      >
        <Flex>
          <Text fontSize="2xl" fontWeight="semibold" mb="40px">
            Excess Concentration
          </Text>
        </Flex>
        {isChartConfigEmpty && (
          <Alert status="success" borderRadius="6px">
            <AlertIcon />
            <AlertDescription fontWeight="semibold">
              There is no excess concentration for receivables within the
              selected date range.
            </AlertDescription>
          </Alert>
        )}
        {!isChartConfigEmpty && activeFields.length === 0 && (
          <Alert status="warning" borderRadius="6px">
            <AlertIcon />
            <AlertDescription fontWeight="semibold">
              Select concentration limits to show data.
            </AlertDescription>
          </Alert>
        )}
        {!isChartConfigEmpty && activeFields.length > 0 && (
          <Box
            padding="40px"
            background="white"
            borderRadius="8px"
            border="none"
          >
            <PortfolioReChart
              chartData={data}
              selectedTimePeriod={selectedTimePeriod}
              currentSelectedBarReportingDate={
                selectedBar
                  ? selectedBar.reportingDate
                  : getDefaultDashboardDisplayEndDate()
              }
              onSelectBar={setSelectedBar}
              chartConfig={chartConfig}
              visibleChartComponents={activeFields}
              tooltipLabelType="date"
            />
          </Box>
        )}
        {!isChartConfigEmpty && selectedBar && (
          <Box width="100%" mt="64px">
            <ExcessConcentrationChartDetailsTable
              selectedBarData={selectedBar}
              chartConfig={chartConfig}
              chartTitle="Excess Concentration"
              activeFields={activeFields}
              onToggleActiveField={(
                selectedField: keyof ConcentrationLimitDataForReportingDate,
              ) =>
                onToggleActiveField<ConcentrationLimitDataForReportingDate>({
                  selectedField,
                  currentActiveFields: activeFields,
                  setActiveFields,
                })
              }
            />
          </Box>
        )}
      </Box>
    </>
  );
};
